import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, CheckCircle, FileText, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');
  const invoiceId = searchParams.get('invoice_id');
  const quoteId = searchParams.get('quote_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('success'); // Assume success if no session ID
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/payment/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentInfo(data);
        setStatus(data.payment_status === 'paid' ? 'success' : 'pending');
      } else {
        setStatus('success'); // Default to success on error
      }
    } catch (err) {
      setStatus('success'); // Default to success on error
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        {status === 'success' ? (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Successful!
            </h1>
            
            <p className="text-gray-600 mb-6">
              Thank you for your payment. A confirmation receipt has been sent to your email.
            </p>

            {paymentInfo && paymentInfo.amount_total > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">Amount Paid</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(paymentInfo.amount_total)}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {invoiceId && (
                <Link to={`/pay/receipt/${invoiceId}`}>
                  <Button className="w-full" variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    View Receipt
                  </Button>
                </Link>
              )}
              
              <p className="text-sm text-gray-400 mt-4">
                You can close this window now.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-10 w-10 text-yellow-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Processing
            </h1>
            
            <p className="text-gray-600 mb-6">
              Your payment is being processed. You'll receive a confirmation email shortly.
            </p>
            
            <p className="text-sm text-gray-400">
              You can close this window now.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
